/* ========================================================================== 
   Capilla del Rosario — Product drawer controller v3
   Opens a remote alternate product template: ?view=drawer
   Adds adaptive gallery framing and touch/click zoom.
   ========================================================================== */
(() => {
  'use strict';

  const drawer = document.querySelector('[data-cpd-drawer]');
  if (!drawer) return;

  const panel = drawer.querySelector('.cpd__panel');
  const body = drawer.querySelector('[data-cpd-body]');
  const footer = drawer.querySelector('[data-cpd-footer]');
  const loading = drawer.querySelector('[data-cpd-loading]');
  const closeButtons = drawer.querySelectorAll('[data-cpd-close]');

  const state = {
    lastTrigger: null,
    productUrl: '',
    productTitle: '',
    variants: [],
    selectedVariant: null,
    selectedOptions: [],
    quantity: 1,
    locale: document.documentElement.lang || 'es',
    isEnglish: (document.documentElement.lang || '').toLowerCase().startsWith('en')
  };

  const strings = {
    loadingError: () => state.isEnglish ? 'We could not load this product. Please try again.' : 'No pudimos cargar este producto. Inténtalo de nuevo.',
    unavailable: () => state.isEnglish ? 'Sold out' : 'Agotado',
    add: () => state.isEnglish ? 'Add to cart' : 'Agregar al carrito',
    added: () => state.isEnglish ? 'Added to cart.' : 'Añadido al carrito.',
    addError: () => state.isEnglish ? 'Something went wrong. Please try again.' : 'Algo salió mal. Inténtalo otra vez.'
  };

  const getRoutesRoot = () => {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (!root) return '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const escapeHtml = (value) => String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  function setLoading(isLoading) {
    if (!loading) return;
    loading.hidden = !isLoading;
  }

  function setDrawerOpen(open) {
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    document.documentElement.classList.toggle('cpd-scroll-lock', open);
    document.body.classList.toggle('cpd-scroll-lock', open);

    if (open) {
      window.setTimeout(() => panel.focus(), 50);
    }
  }

  function closeDrawer() {
    if (!drawer.classList.contains('is-open')) return;
    const zoom = drawer.querySelector('[data-cpd-image-zoom-dialog]');
    if (zoom) zoom.remove();
    setDrawerOpen(false);
    window.setTimeout(() => {
      body.innerHTML = '';
      footer.innerHTML = '';
      state.variants = [];
      state.selectedVariant = null;
      state.selectedOptions = [];
      state.quantity = 1;
      if (state.lastTrigger && typeof state.lastTrigger.focus === 'function') state.lastTrigger.focus();
    }, 260);
  }

  function toDrawerUrl(productUrl) {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('view', 'drawer');
    return url.toString();
  }

  function getRootFromResponse(html) {
    const documentFromResponse = new DOMParser().parseFromString(html, 'text/html');
    return documentFromResponse.querySelector('[data-cpd-product-root]');
  }

  async function openDrawer(productUrl, trigger) {
    if (!productUrl) return;

    state.lastTrigger = trigger || null;
    state.productUrl = productUrl;
    body.innerHTML = '';
    footer.innerHTML = '';
    setDrawerOpen(true);
    setLoading(true);

    try {
      const response = await fetch(toDrawerUrl(productUrl), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!response.ok) throw new Error(`Drawer product request failed (${response.status})`);

      const html = await response.text();
      const remoteRoot = getRootFromResponse(html);
      if (!remoteRoot) throw new Error('Drawer markup was not found in product response.');

      const content = remoteRoot.querySelector('[data-cpd-product-content]');
      const footerTemplate = remoteRoot.querySelector('template[data-cpd-footer-template]');
      const variantsNode = remoteRoot.querySelector('[data-cpd-variants]');

      if (!content || !footerTemplate || !variantsNode) throw new Error('Drawer response is incomplete.');

      body.innerHTML = content.innerHTML;
      footer.innerHTML = footerTemplate.innerHTML;
      body.scrollTop = 0;

      state.productTitle = remoteRoot.dataset.productTitle || '';
      state.variants = JSON.parse(variantsNode.textContent || '[]');
      state.selectedVariant = state.variants.find((variant) => variant.available) || state.variants[0] || null;
      state.selectedOptions = state.selectedVariant ? [...state.selectedVariant.options] : [];
      state.quantity = 1;

      syncDrawer();
    } catch (error) {
      console.error('[Capilla product drawer]', error);
      body.innerHTML = `
        <div class="cpd__error">
          <strong>${escapeHtml(strings.loadingError())}</strong>
          <a href="${escapeHtml(productUrl)}">${escapeHtml(state.isEnglish ? 'View product page' : 'Ver página del producto')}</a>
        </div>
      `;
    } finally {
      setLoading(false);
    }
  }

  function selectVariantFromOptions(changedIndex, changedValue) {
    if (!state.variants.length) return;

    state.selectedOptions[changedIndex] = changedValue;

    let nextVariant = state.variants.find((variant) =>
      variant.options.every((optionValue, index) => optionValue === state.selectedOptions[index])
    );

    if (!nextVariant) {
      nextVariant = state.variants.find((variant) =>
        variant.available && variant.options[changedIndex] === changedValue
      ) || state.variants.find((variant) => variant.options[changedIndex] === changedValue);
    }

    if (!nextVariant) return;

    state.selectedVariant = nextVariant;
    state.selectedOptions = [...nextVariant.options];
    syncDrawer();
  }

  function syncOptions() {
    panel.querySelectorAll('[data-cpd-option-group]').forEach((group) => {
      const optionIndex = Number(group.dataset.optionIndex);
      const selectedValue = state.selectedOptions[optionIndex];

      group.querySelectorAll('[data-cpd-option-value]').forEach((button) => {
        const optionValue = button.dataset.cpdOptionValue;
        const isSelected = optionValue === selectedValue;
        const hasAvailableCombination = state.variants.some((variant) => {
          if (!variant.available || variant.options[optionIndex] !== optionValue) return false;
          return variant.options.every((variantValue, index) => (
            index === optionIndex || variantValue === state.selectedOptions[index]
          ));
        });

        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
        button.disabled = !hasAvailableCombination && !isSelected;
      });
    });
  }

  function updateMainImageFromVariant() {
    if (!state.selectedVariant || !state.selectedVariant.featured_media_id) return;
    const matchingThumb = panel.querySelector(`[data-cpd-gallery-thumb][data-media-id="${state.selectedVariant.featured_media_id}"]`);
    if (matchingThumb) setMainImage(matchingThumb);
  }

  function syncMainMediaRatio(image) {
    const media = panel.querySelector('[data-cpd-main-media]');
    if (!media || !image) return;

    const applyRatio = () => {
      if (!image.naturalWidth || !image.naturalHeight) return;

      /* Mantiene el marco proporcional sin permitir formatos extremos que
         ocupen toda la pantalla en mobile. */
      const rawRatio = image.naturalWidth / image.naturalHeight;
      const uiRatio = Math.min(1.28, Math.max(0.88, rawRatio));
      media.style.setProperty('--cpd-media-ratio', `${uiRatio.toFixed(4)} / 1`);
    };

    if (image.complete && image.naturalWidth) {
      applyRatio();
    } else {
      image.addEventListener('load', applyRatio, { once: true });
    }
  }

  function setMainImage(thumb) {
    const mainImage = panel.querySelector('[data-cpd-main-image]');
    if (!mainImage || !thumb) return;

    mainImage.src = thumb.dataset.imageSrc;
    mainImage.alt = thumb.dataset.imageAlt || state.productTitle;
    syncMainMediaRatio(mainImage);

    panel.querySelectorAll('[data-cpd-gallery-thumb]').forEach((item) => {
      const isActive = item === thumb;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-pressed', String(isActive));
    });
  }

  function closeImageZoom() {
    const zoom = drawer.querySelector('[data-cpd-image-zoom-dialog]');
    if (!zoom) return false;

    zoom.classList.remove('is-open');
    window.setTimeout(() => zoom.remove(), 170);
    return true;
  }

  function openImageZoom() {
    const mainImage = panel.querySelector('[data-cpd-main-image]');
    if (!mainImage || !(mainImage.currentSrc || mainImage.src)) return;

    closeImageZoom();

    const zoom = document.createElement('div');
    zoom.className = 'cpd__image-zoom';
    zoom.dataset.cpdImageZoomDialog = '';
    zoom.setAttribute('role', 'dialog');
    zoom.setAttribute('aria-modal', 'true');
    zoom.setAttribute('aria-label', state.isEnglish ? 'Enlarged product image' : 'Imagen ampliada del producto');

    zoom.innerHTML = `
      <button class="cpd__zoom-close" type="button" data-cpd-zoom-close aria-label="${escapeHtml(state.isEnglish ? 'Close enlarged image' : 'Cerrar imagen ampliada')}">×</button>
      <button class="cpd__zoom-stage" type="button" data-cpd-zoom-stage aria-label="${escapeHtml(state.isEnglish ? 'Zoom image' : 'Ampliar imagen')}">
        <img src="${escapeHtml(mainImage.currentSrc || mainImage.src)}" alt="${escapeHtml(mainImage.alt || state.productTitle)}">
      </button>
    `;

    drawer.appendChild(zoom);

    window.requestAnimationFrame(() => {
      zoom.classList.add('is-open');
      const closeButton = zoom.querySelector('[data-cpd-zoom-close]');
      if (closeButton) closeButton.focus();
    });

    zoom.addEventListener('click', (event) => {
      if (event.target === zoom || event.target.closest('[data-cpd-zoom-close]')) {
        closeImageZoom();
        return;
      }

      const zoomStage = event.target.closest('[data-cpd-zoom-stage]');
      if (zoomStage) {
        zoomStage.classList.toggle('is-zoomed');
      }
    });
  }

  function setText(selector, value) {
    panel.querySelectorAll(selector).forEach((element) => { element.textContent = value || ''; });
  }

  function syncDrawer() {
    const variant = state.selectedVariant;
    if (!variant) return;

    syncOptions();
    updateMainImageFromVariant();
    syncMainMediaRatio(panel.querySelector('[data-cpd-main-image]'));

    setText('[data-cpd-price]', variant.price_formatted);
    setText('[data-cpd-footer-price]', variant.price_formatted);
    setText('[data-cpd-footer-variant]', variant.title === 'Default Title' ? '' : variant.title);
    setText('[data-cpd-qty]', String(state.quantity));

    panel.querySelectorAll('[data-cpd-compare-price]').forEach((element) => {
      const showCompare = Boolean(variant.compare_at_price_formatted);
      element.textContent = variant.compare_at_price_formatted || '';
      element.classList.toggle('is-hidden', !showCompare);
    });

    panel.querySelectorAll('[data-cpd-add]').forEach((button) => {
      button.disabled = !variant.available;
      button.classList.remove('is-success');
      const label = button.querySelector('[data-cpd-add-label]');
      if (label) label.textContent = variant.available ? strings.add() : strings.unavailable();
    });

    panel.querySelectorAll('[data-cpd-status]').forEach((element) => {
      element.textContent = '';
      element.classList.remove('is-error');
    });
  }

  async function refreshCartCount() {
    try {
      const response = await fetch(`${getRoutesRoot()}cart.js`, { credentials: 'same-origin' });
      if (!response.ok) return null;
      const cart = await response.json();

      document.querySelectorAll('[data-cart-count], [data-cart-item-count], .cart-count-bubble span').forEach((node) => {
        node.textContent = String(cart.item_count);
      });

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }));
      return cart;
    } catch (error) {
      console.warn('[Capilla product drawer] Cart count could not be refreshed.', error);
      return null;
    }
  }

  async function addCurrentVariant() {
    const variant = state.selectedVariant;
    if (!variant || !variant.available) return;

    const addButtons = panel.querySelectorAll('[data-cpd-add]');
    const statusNodes = panel.querySelectorAll('[data-cpd-status]');
    addButtons.forEach((button) => { button.disabled = true; });
    statusNodes.forEach((node) => {
      node.textContent = '';
      node.classList.remove('is-error');
    });

    try {
      const response = await fetch(`${getRoutesRoot()}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: state.quantity }]
        })
      });

      if (!response.ok) {
        const problem = await response.json().catch(() => ({}));
        throw new Error(problem.description || problem.message || 'Cart add failed');
      }

      await refreshCartCount();

      addButtons.forEach((button) => {
        button.classList.add('is-success');
        button.disabled = false;
        const label = button.querySelector('[data-cpd-add-label]');
        if (label) label.textContent = strings.added();
      });
      statusNodes.forEach((node) => { node.textContent = strings.added(); });

      window.setTimeout(() => syncDrawer(), 1800);
    } catch (error) {
      console.error('[Capilla product drawer]', error);
      addButtons.forEach((button) => { button.disabled = false; });
      statusNodes.forEach((node) => {
        node.textContent = strings.addError();
        node.classList.add('is-error');
      });
    }
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-capilla-product-drawer]');
    if (!trigger) return;

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1) return;

    const productUrl = trigger.dataset.productUrl || trigger.getAttribute('href');
    if (!productUrl) return;

    event.preventDefault();
    openDrawer(productUrl, trigger);
  });

  closeButtons.forEach((button) => button.addEventListener('click', closeDrawer));

  panel.addEventListener('click', (event) => {
    const imageZoomTrigger = event.target.closest('[data-cpd-image-zoom]');
    if (imageZoomTrigger) {
      openImageZoom();
      return;
    }

    const thumbnail = event.target.closest('[data-cpd-gallery-thumb]');
    if (thumbnail) {
      setMainImage(thumbnail);
      return;
    }

    const optionButton = event.target.closest('[data-cpd-option-value]');
    if (optionButton && !optionButton.disabled) {
      selectVariantFromOptions(Number(optionButton.dataset.optionIndex), optionButton.dataset.cpdOptionValue);
      return;
    }

    const descriptionToggle = event.target.closest('[data-cpd-description-toggle]');
    if (descriptionToggle) {
      const descriptionWrap = descriptionToggle.closest('.cpd-product__description-wrap');
      const preview = descriptionWrap && descriptionWrap.querySelector('[data-cpd-description-preview]');
      const full = descriptionWrap && descriptionWrap.querySelector('[data-cpd-description-full]');
      const isExpanded = descriptionToggle.getAttribute('aria-expanded') === 'true';

      if (preview && full) {
        preview.hidden = !isExpanded;
        full.hidden = isExpanded;
        descriptionToggle.setAttribute('aria-expanded', String(!isExpanded));
        descriptionToggle.textContent = !isExpanded
          ? (descriptionToggle.dataset.lessLabel || (state.isEnglish ? 'Read less' : 'Ver menos'))
          : (descriptionToggle.dataset.moreLabel || (state.isEnglish ? 'Read more' : 'Ver más'));
      }
      return;
    }

    if (event.target.closest('[data-cpd-qty-minus]')) {
      state.quantity = Math.max(1, state.quantity - 1);
      setText('[data-cpd-qty]', String(state.quantity));
      return;
    }

    if (event.target.closest('[data-cpd-qty-plus]')) {
      state.quantity += 1;
      setText('[data-cpd-qty]', String(state.quantity));
      return;
    }

    if (event.target.closest('[data-cpd-add]')) {
      addCurrentVariant();
    }
  });

  document.addEventListener('keydown', (event) => {
    const trigger = event.target && event.target.closest
      ? event.target.closest('[data-capilla-product-drawer]')
      : null;

    if (trigger && (event.key === 'Enter' || event.key === ' ')) {
      if (trigger.tagName !== 'A') {
        event.preventDefault();
        const productUrl = trigger.dataset.productUrl || trigger.getAttribute('href');
        if (productUrl) openDrawer(productUrl, trigger);
        return;
      }
    }

    if (event.key === 'Escape') {
      if (closeImageZoom()) return;
      if (drawer.classList.contains('is-open')) closeDrawer();
    }
  });
})();
